import { ReactNode } from 'react';
import { useSwipeableTabs } from '@/hooks/useSwipeableTabs';

interface SwipeableTabsProps {
  currentTab: string;
  tabs: string[];
  onTabChange: (tab: string) => void;
  children: ReactNode[];
  className?: string;
}

/**
 * Swipeable tabs container for mobile.
 * On desktop, tabs behave normally. On mobile, content can be swiped horizontally.
 */
export function SwipeableTabs({ currentTab, tabs, onTabChange, children, className = '' }: SwipeableTabsProps) {
  const { containerRef, style, isDragging } = useSwipeableTabs({
    currentTab,
    tabs,
    onTabChange,
    enabled: true,
  });

  return (
    <div className={`relative ${className}`}>
      {/* Mobile: Swipeable container */}
      <div className="overflow-hidden md:hidden">
        <div
          ref={containerRef}
          style={{
            ...style,
            touchAction: 'pan-y', // Allow vertical scroll, prevent horizontal
          }}
          className={`flex select-none ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        >
          {children.map((child, index) => (
            <div
              key={tabs[index]}
              className="box-border min-w-full flex-shrink-0"
              style={{
                width: '100%',
                marginRight: index < children.length - 1 ? '1rem' : '0',
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Desktop: Normal tabs */}
      <div className="hidden md:block">
        {children.map((child, index) => (
          <div key={tabs[index]} style={{ display: currentTab === tabs[index] ? 'block' : 'none' }}>
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}
